import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { CustomerDepartmentCreateRequest, ShiftTimeRequest, UserItemResponse } from '../../../api';
import { BaseToastAlertClasses } from '../../shared/enums/base-toast-alert-classes.enum';
import { BaseTokenInterface } from '../../shared/interfaces/base-token-service.interface';
import { baseAdministrationActions, baseCustomerDepartmentActions, baseSharedActions } from '../actions';
import { BaseCoreState } from '../reducers';
import { baseAuthSelectors } from '../selectors';
import { BaseCustomerDepartmentService } from '../../shared/services/base-customer-department.service';

@Injectable()
export class BaseCustomerDepartmentEffects {
  loadCustomerDepartment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseCustomerDepartmentActions.loadCustomerDepartment),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.departmentService
          .getCustomerDepartment({
            customer: user.organizationId,
            department: action.payload.department,
          })
          .pipe(
            mergeMap((response) => [
              baseCustomerDepartmentActions.setCustomerDepartment({ payload: response.data }),
              baseCustomerDepartmentActions.successCustomerDepartment(),
            ]),
            catchError(() => of(baseCustomerDepartmentActions.errorCustomerDepartment()))
          );
      })
    );
  });

  copyCustomerDepartment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseCustomerDepartmentActions.copyCustomerDepartment),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.departmentService
          .getCustomerDepartment({
            customer: user.organizationId,
            department: action.payload.department,
          })
          .pipe(
            mergeMap(({ data }) => {
              const body: CustomerDepartmentCreateRequest = {
                billingContact: data.billingContact,
                businessAddress: data.businessAddress,
                primaryData: {
                  name: `${data.primaryData.name} (Copy)`,
                },
                contact: data.contact,
                requirements: data.requirements?.map((department) => department.id) || [],
                users: data.users,
                shiftTimes: data.shiftTimes as ShiftTimeRequest[],
              };
              this.store.dispatch(
                baseCustomerDepartmentActions.createDepartmentCrud({
                  payload: {
                    location: action.payload.location,
                    body,
                    search: action.payload.searchTerm,
                  },
                })
              );

              return [
                baseCustomerDepartmentActions.successCopyCustomerDepartment(),
                baseSharedActions.addSystemAlert({
                  payload: {
                    class: BaseToastAlertClasses.success,
                    body: this.translateService.instant('department.copyDepartmentToastMessage', {
                      name: data.primaryData.name,
                    }),
                  },
                }),
              ];
            }),
            catchError(() => of(baseCustomerDepartmentActions.errorCopyCustomerDepartment()))
          );
      })
    );
  });

  loadCustomerUsers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseCustomerDepartmentActions.loadCustomerUsers),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.departmentService
          .getCustomerUsers({
            customer: user.organizationId,
            roles: action.payload?.roles,
            active: action.payload?.active,
            search: action.payload?.search,
          })
          .pipe(
            mergeMap((response: UserItemResponse[]) => [
              baseCustomerDepartmentActions.setCustomerUsers({ payload: response }),
              baseCustomerDepartmentActions.successCustomerUsers(),
            ]),
            catchError(() => of(baseCustomerDepartmentActions.errorCustomerUsers()))
          );
      })
    );
  });

  loadCustomerActiveUserList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseCustomerDepartmentActions.loadCustomerActiveUserList),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.departmentService
          .getCustomerUsers({
            customer: user.organizationId,
            search: action.payload?.search,
            active: true,
            roles: ['user'],
          })
          .pipe(
            mergeMap((response) => [
              baseCustomerDepartmentActions.setCustomerActiveUserList({ payload: response }),
              baseCustomerDepartmentActions.successCustomerActiveUserList(),
            ]),
            catchError(() => of(baseCustomerDepartmentActions.errorCustomerActiveUserList()))
          );
      })
    );
  });

  updateUsersToCustomerDepartment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseCustomerDepartmentActions.updateUsersToCustomerDepartment),
      switchMap((action) => {
        return this.departmentService
          .updateUsersToCustomerDepartment(action.payload)
          .pipe(
            mergeMap(() => [
              baseSharedActions.loadCustomerDepartmentList({ payload: {} }),
              baseAdministrationActions.loadLocationsData({ payload: {} }),
            ])
          );
      })
    );
  });

  updateCustomerDepartmentMasterData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseCustomerDepartmentActions.updateCustomerDepartmentMasterData),
      switchMap((action) => {
        return this.departmentService
          .updateMasterData(action.payload)
          .pipe(
            mergeMap(() => [
              baseSharedActions.loadCustomerDepartmentList({ payload: {} }),
              baseAdministrationActions.loadLocationsData({ payload: { search: action.payload.search } }),
            ])
          );
      })
    );
  });

  updateCustomerDepartmentShiftTimes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseCustomerDepartmentActions.updateCustomerDepartmentShiftTimes),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.departmentService
          .updateShiftTimes({
            customer: user.organizationId,
            department: action.payload.department,
            location: action.payload.location,
            body: action.payload.body,
          })
          .pipe(
            mergeMap(() => [
              baseCustomerDepartmentActions.successUpdateCustomerDepartmentShiftTimes(),
              baseSharedActions.loadCustomerDepartmentList({ payload: {} }),
              baseAdministrationActions.loadLocationsData({ payload: {} }),
              baseCustomerDepartmentActions.loadCustomerDepartment({
                payload: {
                  department: action.payload.department,
                },
              }),
            ]),
            catchError(() => of(baseCustomerDepartmentActions.errorUpdateCustomerDepartmentShiftTimes()))
          );
      })
    );
  });

  updateCustomerDepartmentRequirements$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseCustomerDepartmentActions.updateCustomerDepartmentRequirements),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.departmentService
          .updateRequirements({
            customer: user.organizationId,
            location: action.payload.location,
            department: action.payload.department,
            body: action.payload.body,
          })
          .pipe(
            mergeMap(() => [
              baseCustomerDepartmentActions.successUpdateCustomerDepartmentRequirements(),
              baseCustomerDepartmentActions.loadCustomerDepartment({
                payload: {
                  department: action.payload.department,
                },
              }),
            ]),
            catchError(() => of(baseCustomerDepartmentActions.errorUpdateCustomerDepartmentRequirements()))
          );
      })
    );
  });

  deleteCustomerDepartmentRequirement$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseCustomerDepartmentActions.deleteCustomerDepartmentRequirement),
      concatMap((action) => {
        return this.departmentService.deleteRequirement(action.payload).pipe(
          mergeMap(() => [baseCustomerDepartmentActions.successDeleteCustomerDepartmentRequirement()]),
          catchError(() => of(baseCustomerDepartmentActions.errorDeleteCustomerDepartmentRequirement()))
        );
      })
    );
  });

  createDepartmentCrud$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseCustomerDepartmentActions.createDepartmentCrud),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.departmentService
          .createDepartment(user.organizationId, action.payload.location, action.payload.body)
          .pipe(
            mergeMap((createdDepartment) => [
              baseCustomerDepartmentActions.setDepartmentCrud({ payload: createdDepartment }),
              baseSharedActions.loadCustomerDepartmentList({ payload: {} }),
              baseAdministrationActions.loadLocationsData({ payload: { search: action.payload.search } }),
              baseCustomerDepartmentActions.successDepartmentCrud(),
            ]),
            catchError(() => of(baseCustomerDepartmentActions.errorDepartmentCrud()))
          );
      })
    );
  });

  addFavouriteQualifications$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseCustomerDepartmentActions.addFavouriteQualifications),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.departmentService
          .addFavouriteQualifications({
            customer: user.organizationId,
            department: action.payload.department,
            location: action.payload.location,
            body: action.payload.body,
          })
          .pipe(
            tap(() => {
              if (action.payload.callback) {
                action.payload.callback();
              }
            }),
            mergeMap(() => [
              baseCustomerDepartmentActions.addFavouriteQualificationsSuccess(),
              baseSharedActions.addSystemAlert({
                payload: {
                  class: BaseToastAlertClasses.success,
                  body: 'favorite.success',
                },
              }),
              baseSharedActions.loadCustomerDepartmentFavouriteQualificationList({
                payload: {
                  department: action.payload.department,
                  location: action.payload.location,
                },
              }),
            ]),
            catchError(() => of(baseCustomerDepartmentActions.addFavouriteQualificationsError()))
          );
      })
    );
  });

  deleteFavouriteQualification$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseCustomerDepartmentActions.deleteFavouriteQualification),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.departmentService
          .deleteFavouriteQualification({
            customer: user.organizationId,
            department: action.payload.department,
            location: action.payload.location,
            qualification: action.payload.qualification,
          })
          .pipe(
            tap(() => {
              if (action.payload.callback) {
                action.payload.callback();
              }
            }),
            mergeMap(() => [
              baseCustomerDepartmentActions.deleteFavouriteQualificationSuccess(),
              baseSharedActions.loadCustomerDepartmentFavouriteQualificationList({
                payload: {
                  department: action.payload.department,
                  location: action.payload.location,
                },
              }),
            ]),
            catchError(() => of(baseCustomerDepartmentActions.deleteFavouriteQualificationError()))
          );
      })
    );
  });

  updateDepartmentQualificationDescription$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseCustomerDepartmentActions.updateDepartmentQualificationDescription),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.departmentService
          .updateDepartmentQualificationDescription({
            qualification: action.payload.qualification,
            customer: user?.organizationId,
            department: action.payload.department,
            location: action.payload.location,
            body: action.payload.body,
          })
          .pipe(
            mergeMap(() => [
              baseSharedActions.loadCustomerDepartmentQualificationsList({
                payload: {
                  department: action.payload.department,
                },
              }),
              baseSharedActions.loadCustomerDepartmentFavouriteQualificationList({
                payload: {
                  department: action.payload.department,
                  location: action.payload.location,
                },
              }),
            ])
          );
      })
    );
  });

  loadDepartmentDutyModels$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseCustomerDepartmentActions.loadDepartmentDutyModels),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.departmentService
          .getDutyModels({
            customer: user.organizationId,
            location: action.payload.location,
            department: action.payload.department,
          })
          .pipe(
            mergeMap((response) => [
              baseCustomerDepartmentActions.setDepartmentDutyModels({ payload: response }),
              baseCustomerDepartmentActions.successDepartmentDutyModels(),
            ]),
            catchError(() => of(baseCustomerDepartmentActions.errorDepartmentDutyModels()))
          );
      })
    );
  });

  setDutyModelAdoption$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseCustomerDepartmentActions.setDutyModelAdoption),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      // mergeMap: toggling two models quickly must send both requests, not cancel the first one.
      mergeMap(([action, user]) => {
        const { location, department } = action.payload;

        return this.departmentService.setDutyModelAdoption({ customer: user.organizationId, ...action.payload }).pipe(
          mergeMap(() => [
            baseCustomerDepartmentActions.successDutyModelAdoption(),
            baseCustomerDepartmentActions.loadDepartmentDutyModels({ payload: { location, department } }),
            baseSharedActions.loadCustomerDepartmentList({ payload: {} }),
            baseSharedActions.addSystemAlert({
              payload: {
                class: BaseToastAlertClasses.success,
                body: 'successMessage.changesSaved',
              },
            }),
          ]),
          // The interceptor toasts the error; the reload puts the switch back to the server state.
          catchError(() =>
            of(
              baseCustomerDepartmentActions.errorDutyModelAdoption(),
              baseCustomerDepartmentActions.loadDepartmentDutyModels({ payload: { location, department } })
            )
          )
        );
      })
    );
  });

  constructor(
    private actions$: Actions,
    private store: Store<BaseCoreState>,
    private translateService: TranslateService,
    private readonly departmentService: BaseCustomerDepartmentService
  ) {}
}
